/**
 * =============================================================================
 * LOGGER UTILITY - STOREFRONT WIDGET
 * =============================================================================
 *
 * Structured logging utility for the storefront widget.
 * Prefixes all messages with [Find My Part] for easy filtering in browser console.
 *
 * In production builds, all console calls are stripped via terser drop_console.
 *
 * Features:
 * - Environment-aware log level filtering (debug only in development)
 * - Structured context support for better debugging
 * - Error object handling with stack traces
 * - Consistent prefix formatting for browser console filtering
 *
 * =============================================================================
 */

const PREFIX = '[Find My Part]';
const isDevelopment = import.meta.env?.MODE === 'development';

/**
 * Structured context for log entries.
 */
export type LogContext = Record<string, unknown>;

/**
 * Logger interface matching the worker logger pattern.
 */
export interface Logger {
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: Error | null, context?: LogContext) => void;
}

/**
 * Format context object for console output.
 * Returns empty string if context is empty or undefined.
 */
function formatContext(context?: LogContext): string {
  if (!context || Object.keys(context).length === 0) return '';
  return ' ' + JSON.stringify(context);
}

/**
 * Storefront widget logger instance.
 *
 * @example
 * ```ts
 * import { logger } from '../lib/logger';
 *
 * // Debug logging (development only)
 * logger.debug('STORE_CONTEXT_DETECTED', { storeHash: 'abc123' });
 *
 * // Info logging
 * logger.info('CONFIG_LOADED', { turnstileSiteKey: 'xxx' });
 *
 * // Warning logging
 * logger.warn('SHADOW_DOM_UNAVAILABLE');
 *
 * // Error logging with Error object
 * logger.error('CONFIG_LOAD_FAILURE', error, { endpoint: '/api/config' });
 * ```
 */
export const logger: Logger = {
  /**
   * Log debug message (development only).
   * Suppressed in production builds.
   */
  debug(message: string, context?: LogContext): void {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`${PREFIX} [DEBUG] ${message}${formatContext(context)}`);
    }
  },

  /**
   * Log info message.
   * In production builds, stripped via drop_console.
   */
  info(message: string, context?: LogContext): void {
    // eslint-disable-next-line no-console
    console.log(`${PREFIX} ${message}${formatContext(context)}`);
  },

  /**
   * Log warning message.
   * In production builds, stripped via drop_console.
   */
  warn(message: string, context?: LogContext): void {
    // eslint-disable-next-line no-console
    console.warn(`${PREFIX} ${message}${formatContext(context)}`);
  },

  /**
   * Log error message with optional Error object.
   * Error stack traces are included in context when provided.
   * In production builds, stripped via drop_console.
   */
  error(message: string, error?: Error | null, context?: LogContext): void {
    const errorInfo = error ? { errorMessage: error.message, stack: error.stack } : {};
    const fullContext = { ...context, ...errorInfo };
    // eslint-disable-next-line no-console
    console.error(`${PREFIX} ${message}${formatContext(fullContext)}`);
  },
};
